import { useState, useEffect } from 'react';
import { Pill } from '@smarterservices/smarter-ui';
import TimeElapsed from '../Common/TimeElapsed';

/**
 * WaitingTimer Component
 * 
 * Displays a timer that shows how long a session has been waiting.
 * The timer updates at a specified interval or shows static time if updateInterval is 0.
 * Formats the time as MM:SS. If startTime is null or undefined, displays 'n/a'.
 * 
 * @param {Object} props
 * @param {string|Date|null} props.startTime - The time when the session started waiting (ISO string, Date object, or null)
 * @param {number} [props.updateInterval=1000] - How often to update the timer in milliseconds (default: 1 second). Set to 0 for static time.
 * @param {number} [props.highlightAfterSeconds=60] - Number of seconds after which to highlight the timer
 * @returns {JSX.Element}
 * 
 * @example
 * <WaitingTimer startTime={session.createdAt} />
 * <WaitingTimer startTime={session.createdAt} updateInterval={0} /> // Static time, no updates
 * <WaitingTimer startTime={session.createdAt} updateInterval={5000} /> // Update every 5 seconds
 * <WaitingTimer startTime="2024-01-01T12:00:00Z" highlightAfterSeconds={120} />
 * <WaitingTimer startTime={null} /> // Displays 'n/a'
 */
function WaitingTimer({ startTime, updateInterval = 1000, highlightAfterSeconds = 60 }) {
    const [elapsedTime, setElapsedTime] = useState('0:00');
    const [highlight, setHighlight] = useState(false);

    useEffect(() => {
        // If no startTime provided, display n/a
        if (!startTime) {
            setElapsedTime('n/a');
            return;
        }

        /**
         * Calculates the elapsed time from startTime to now
         * @returns {string} Formatted time string (M:SS or MM:SS)
         */
        function calculateElapsedTime() {
            const start = new Date(startTime);
            const now = new Date();
            const diffInSeconds = Math.floor((now - start) / 1000);

            const minutes = Math.floor(diffInSeconds / 60);
            const seconds = diffInSeconds % 60;

            if (diffInSeconds > highlightAfterSeconds && !highlight) {
                setHighlight(true);
            }

            // Format as M:SS or MM:SS
            return `${minutes}:${seconds.toString().padStart(2, '0')}`;
        }

        // Calculate initial time
        setElapsedTime(calculateElapsedTime());

        // Only set up interval if updateInterval is greater than 0
        if (updateInterval > 0) {
            // Update at the specified interval
            const intervalId = setInterval(() => {
                setElapsedTime(calculateElapsedTime());
            }, updateInterval);

            // Cleanup interval on unmount
            return () => clearInterval(intervalId);
        }
    }, [startTime, updateInterval, highlightAfterSeconds]);

    return <Pill
        color={highlight ? 'danger' : 'primary'}
        themeOverride={{
            textFontSize: '0.7rem'
        }}>{elapsedTime}</Pill>;
}

export default WaitingTimer;
